// Default landing view for the live preview: a table of saved records
// with Add/Edit/Delete actions that always work (even when the
// metadata declares no actions).
//
// The grid is augmented with the shared embed-grid UX features:
// search, per-column filter, multi-column sort, full-screen toggle,
// and (when provided via columnTotals) a footer totals row.
//
// ── why every row lookup goes through columnFormKey ──────────────────────
// The engine's list SELECT aliases each projected column to its FORM KEY
// (executor._build_select_list / _column_form_key), not to its db_name —
// because a form that joins a master can hold two columns with the same
// db_name (RENEWER_NAME and APPROVER_NAME both read EMPLOYEE_NAME off two
// aliased joins of the employee master), and keying by db_name collapses
// them onto one slot. So a row arrives as { renewer_name, approver_name },
// and a cell that asked for row['employee_name'] rendered blank while the
// record popup — which reads form state by form key — showed the value.
// Search / filter / sort / totals run in-memory over these same rows, so
// they take the form key too.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDownAZ, ArrowUpAZ, ArrowUpDown, Eye, FileText, History, Mail, Maximize2, Minimize2, Pencil, Play, Plus, RefreshCcw, Rows3, Rows4, Search, Tags, Trash2, UserPlus, Waypoints, X } from 'lucide-react'
import RowActionsMenu from './RowActionsMenu'
import { useEnforcedFilterParts, type OverrideMap } from './EnforcedFilterControls'
import type { ActionDef, ColumnDef, SqlModel } from '../types'
import type {
  EnforcedConditionEcho, EnforcedFilterSetEcho, FilterNotice,
} from '../contracts'
import {
  columnDbName, columnFormKey, ddlbDisplayLabel, listOrderedColumns,
  hasImageUpload, hasDocumentUpload, hasUrlWidget,
  richCellText, listHeadingOf,
} from '../utils'
import { DisplayCell, displayPlainText, hasDisplayFormat, temporalCellText } from '../displayFormat'
import { personLabel, usePeople, type PeopleDirectory } from '../people'
import { openRecord, rowEditable, rowHiddenActions, rowLockReason, visibleRowActions } from '../utils'
import { useKeyboardShortcuts, type Shortcut } from '../keyboard'
import { actionShortcutBindings, actionShortcutCombo, shortcutHint, systemCombo, withShortcutHint } from '../actionShortcuts'
import SplitScrollTable, { type SplitScrollHandle } from '../SplitScrollTable'
import { displayNameForUrl } from '../widgets/FileUploadField'
import type {
  EmbedColumnTotal, EmbedGridUx,
} from '@twasta/page-schema'
import ListTabs from './ListTabRow'
import { HEAD_ANIMATION_MS, prefersReducedMotion, useScrollAwayHeader } from './useScrollAwayHeader'
import ListToolbar from './ListToolbar'
import { isMobileViewport } from '../compact/useIsMobile'
import { CompactSummaryCard } from '../compact/CompactRecordCard'
import type { MoreMenuItem } from '../../customize/ui'
import type { TabEcho } from './listTabs'
import { ALL_TAB_ID } from './listScreenConfig'
import AddFilterButton from './AddFilterButton'
import ColumnTotalsRow from './ColumnTotalsRow'
import { applyGridState } from './applyGridState'
import { useGridState } from './useGridState'
import {
  colKeyOf, orderedDisplayCols, widthOf, useColumnDnd, useResolvedGridLayout,
  RESIZE_HANDLE_STYLE, type GridLayoutApi,
} from './columnLayout'
import { LayoutActions } from './LayoutActions'
import PaginationBar, { type ListPagination } from './PaginationBar'
import { tw } from '../../testHooks'
import { screenMargin, useObjectChrome } from '../../objectChrome'
import { useTxnCustomize } from '../../customize/txnContext'
import { colPath as czColPath, formPath as czFormPath, setLeaf as czSetLeaf } from '../../customize/txnOps'
import { CustomizedDot, GearButton, HiddenTray } from '../../customize/ui'
import { isListHidden } from '../utils'

interface RowShape extends Record<string, unknown> {
  _txn_id: string
}

interface Props {
  model: SqlModel
  rows: RowShape[]
  total: number
  busy: boolean
  /** Draw the object's name at the head of the toolbar. Defaults TRUE — on a
   *  surface with no chrome of its own (the designer's Live Preview) this is
   *  the only thing naming the object. The host passes false when the
   *  surrounding shell already names it; the record count and every control
   *  stay either way. See ../../objectChrome. */
  showTitle?: boolean
  // Live column layout (drag to resize / reorder). When a host supplies a
  // `gridLayout` + this list's `formNo`, the change can be applied back to the
  // design (author only); otherwise it's a local, per-session view tweak.
  gridLayout?: GridLayoutApi
  formNo?: string
  selectedTxnId?: string | null
  // ── End-user rights gating (standalone runtime) ─────────────────────────
  // Both default TRUE so existing hosts (designer preview / platform Run) are
  // unchanged. When false the corresponding affordance is hidden entirely —
  // this is UX on top of the server-side authorization, not the gate itself.
  /** Show the Add button (+ its F3 shortcut). */
  canAdd?: boolean
  /** Show the per-row Delete action (+ its Ctrl+Delete shortcut). */
  canDelete?: boolean
  onAdd: () => void
  onEdit: (txnId: string) => void
  // When set, clicking (or double-clicking) a row opens it READ-ONLY through
  // this instead of for editing — Vision's browse screen, where only the Edit
  // control (pencil / F4 / ⋮ Edit) opens a record in edit mode.
  onView?: (txnId: string) => void
  onDelete: (txnId: string) => void
  // When set, a History action appears per row (opens the record's audit
  // timeline). Omitted on surfaces that don't surface history.
  onHistory?: (txnId: string) => void
  // When set, a "Messages" action appears per row (the record's message log —
  // only for an object that sends messages).
  onMessages?: (txnId: string) => void
  // When set, an "Also known as" action appears per row. Present only for a
  // vector-searchable MASTER whose model allows aliases — teaching an alias is
  // meaningless on an object nothing searches semantically.
  onAlias?: (txnId: string) => void
  // When set, an "Assign to…" action appears per row: hand THIS record to a
  // person or a role, so it lands in their work list opened on the record.
  // The human-authored twin of a work rule, and the reason a colleague can be
  // given something no rule anticipated. Absent on hosts with no work panel.
  onAssign?: (txnId: string) => void
  // When set, a "Process trail" action appears per row: this record's chain
  // across the objects it is linked to. A trail is about ONE record, so it
  // lives here and never on the toolbar, where nothing is selected yet. Set
  // only when the object is actually placed on a process flow.
  onProcessTrail?: (txnId: string) => void
  onRefresh: () => void
  // ── Custom action buttons (scope-aware placement) ───────────────────────
  // Form-scoped actions are rendered as buttons just before "Add"; row-scoped
  // actions are added to each row's 3-dot (⋮) menu and run against that row.
  // The host owns execution (running the engine action + applying the result).
  formActions?: ActionDef[]
  rowActions?: ActionDef[]
  onRunFormAction?: (name: string) => void
  onRunRowAction?: (name: string, txnId: string) => void
  // ── Bulk actions (multi-row select) ─────────────────────────────────────
  // When `bulkActions` is non-empty and `onRunBulkAction` is wired, a leading
  // checkbox column + a selection bar appear: the user ticks rows and runs one
  // of these actions against the whole selection at once. The host owns
  // execution (the one-time confirmation gate + the results modal). Pass the
  // bulk-eligible subset of the row actions (see bulkEligibleActions).
  bulkActions?: ActionDef[]
  onRunBulkAction?: (name: string, txnIds: string[]) => void
  bulkBusy?: boolean
  // Select-all-across-pages: when supplied, the selection bar offers a "Select
  // all N" link (once every loaded row is ticked and more match on the server)
  // that resolves EVERY matching record's txn_id under the current server-side
  // filters. Returns the ids (the host toasts if the set was capped). Omitted ⇒
  // selection is limited to the loaded rows.
  onSelectAllMatching?: () => Promise<string[]>
  // System add / edit actions merge into the built-in controls instead of
  // rendering as extra buttons: ``add`` renames the "Add" button, ``edit``
  // becomes the row pencil's hover text. Clicking the built-in then routes
  // through onRunFormAction / onRunRowAction so the action's pre-action
  // validations run before the operation (see SystemActionOverrides).
  systemOverrides?: { add?: ActionDef; edit?: ActionDef; view?: ActionDef; delete?: ActionDef }
  /**
   * When set, single row click invokes onSelect (typically: bind
   * selection variables) and double-click invokes onEdit. When omitted,
   * single click falls back to onEdit so existing usages — e.g. the
   * standalone Live Preview tab — keep their original behaviour.
   * Smart-page embed previews supply this so a row click is just a
   * selection and the editor opens only on explicit double-click.
   */
  onSelect?: (row: RowShape) => void
  // When false, the list's keyboard shortcuts (F3 add / F4 edit / Ctrl+Delete
  // / Ctrl+N · Ctrl+P paging / Ctrl+F search) are suspended — hosts pass
  // `!editorOpen` so the record editor's own bindings (Ctrl+S, F2, …) take
  // over while a record is open. Default on.
  shortcutsEnabled?: boolean
  // Extra controls rendered in the header action group, after "Add".
  // The Live Preview tab uses this to host its deploy menu; embed
  // previews leave it unset.
  headerActions?: React.ReactNode
  // Controls rendered just after "Refresh" and before "Add" — the natural
  // home for the Approvals inbox button so it reads next to the primary list
  // actions rather than trailing the overflow menu.
  beforeAddActions?: React.ReactNode
  // Optional control rendered on the right of the search/filter toolbar
  // row (GridToolbar.rightSlot). Smart-page embeds use this to host the
  // runtime interface switcher (List / Multi-tabular / Form-input).
  viewSwitcher?: React.ReactNode
  // Imperative control of this list's split-scroll table (e.g. a "Freeze
  // first column" menu item in the host's header overflow menu).
  splitHandle?: React.Ref<SplitScrollHandle>
  // Grid UX configuration. Omitted = everything default-on.
  gridUx?: EmbedGridUx
  // Per-column totals shown in the footer.
  columnTotals?: Record<string, EmbedColumnTotal>
  // Designer-enforced conditions currently in effect (resolved values from
  // the server). When set, a form-like filter bar is shown above the grid:
  // the end user can change each condition's value (unless `locked`) but
  // cannot remove it.
  enforcedFilters?: EnforcedConditionEcho[]
  onApplyFilters?: (overrides: OverrideMap) => void
  // Non-blocking messages ('W'/'P') from the conditions' own validations.
  filterNotices?: FilterNotice[]
  // Project folder + object name. Only the enforced-filter bar needs them,
  // and only to open a condition's reference picker.
  project?: string
  objName?: string
  // Designer-defined filter sets the end user can switch between, plus a
  // built-in "No filter (all records)" bypass. When set (and onSelectFilterSet
  // is provided) a set picker is shown above the condition inputs.
  filterSets?: EnforcedFilterSetEcho[]
  activeFilterSetId?: string | null
  bypassed?: boolean
  onSelectFilterSet?: (sel: { setId?: string; bypass?: boolean }) => void
  // Server-side pagination. When only the infinite fields are supplied, a
  // "Load earlier entries" button appends the next (older) page. When the
  // optional dual-mode fields are also supplied, the footer additionally
  // offers a page-size selector and an Infinite ⇆ Paged toggle; in Paged mode
  // Prev/Next replace the visible rows with exactly one server page (keeping
  // the DOM bounded). Sort/search remain client-side over the loaded rows.
  // The bar itself is <PaginationBar>, shared with every other view of the
  // same records — see that module.
  pagination?: ListPagination
  // An extra read-only column just before the row actions, drawn per row by
  // the host — the additional attributes' Flags (flag icons a rule raises).
  // Absent ⇒ no column.
  rowAdornment?: (row: RowShape) => React.ReactNode
  rowAdornmentHeading?: string
  // The full screen the expand button drives, when the host owns one for a
  // surface larger than this list (Customize mode: its bar and field panel go
  // full screen WITH the list). Absent ⇒ the shell's, else the list's own.
  fullScreen?: { on: boolean; set: (on: boolean) => void }
  // ── List tabs (list_view.tabs) ──────────────────────────────────────────
  // The tab row above the toolbar: each tab with its record count, the list
  // layout switch at its end. Absent / empty ⇒ no tab row, and the layout
  // switch ends the toolbar row instead.
  tabs?: TabEcho[] | null
  activeTab?: string | null
  onSelectTab?: (id: string) => void
  showTabCounts?: boolean
  /** The screen's ⋮ menu, handed this list's own items (row density) to
   *  carry. Absent ⇒ `headerActions` as given and a density button. */
  renderMore?: (items: MoreMenuItem[]) => React.ReactNode
  /** The list layout switch's part of the ⋮ menu (`layoutMenuItems`) — put
   *  before the list's own items when `renderMore` draws the menu. */
  layoutMenuItems?: MoreMenuItem[]
  /** The screen's actions placed in the ⋮ menu (Export, form buttons — see
   *  ScreenActions): first in the list's ⋮ when `renderMore` draws it, else
   *  a ⋮ of their own on the toolbar. */
  actionMenuItems?: MoreMenuItem[]
  /** 'cards': the records as summary cards in a grid (the desktop Compact
   *  layout) under the same header, tabs, toolbar and page bar. */
  body?: 'table' | 'cards'
  /** The transaction screen's own frame: the header on the page background
   *  and everything else in a card under it. Off (an embed preview) ⇒ one
   *  flat surface. */
  framed?: boolean
}

/** The head's fold: a grid row animated between its height and none. */
function headFoldStyle(hidden: boolean, clip: boolean): React.CSSProperties {
  const ms = prefersReducedMotion() ? 0 : HEAD_ANIMATION_MS
  return {
    display: 'grid', flex: 'none',
    gridTemplateRows: hidden ? '0fr' : '1fr',
    transition: ms ? `grid-template-rows ${ms}ms cubic-bezier(.2,.7,.2,1)` : 'none',
    overflow: clip ? 'hidden' : 'visible',
  }
}

function headInnerStyle(hidden: boolean, clip: boolean): React.CSSProperties {
  const ms = prefersReducedMotion() ? 0 : HEAD_ANIMATION_MS
  return {
    minHeight: 0, overflow: clip ? 'hidden' : 'visible',
    opacity: hidden ? 0 : 1,
    transform: hidden ? 'translateY(-6px)' : 'none',
    transition: ms ? `opacity ${ms}ms ease, transform ${ms}ms ease` : 'none',
  }
}

/** Width of the host-drawn adornment column (`rowAdornment`). */
const ADORNMENT_COL_WIDTH = 96

export default function ListView({
  model, rows, total, busy, showTitle = true, gridLayout, formNo, selectedTxnId,
  canAdd = true, canDelete = true,
  onAdd, onEdit, onView, onDelete, onHistory, onMessages, onAlias, onAssign, onProcessTrail, onRefresh, onSelect,
  formActions, rowActions, onRunFormAction, onRunRowAction, systemOverrides,
  bulkActions, onRunBulkAction, bulkBusy, onSelectAllMatching,
  headerActions, beforeAddActions, viewSwitcher, splitHandle, gridUx, columnTotals, enforcedFilters, onApplyFilters,
  filterSets, activeFilterSetId, bypassed, onSelectFilterSet, pagination,
  filterNotices, project, objName,
  shortcutsEnabled, rowAdornment, rowAdornmentHeading, fullScreen,
  tabs, activeTab, onSelectTab, showTabCounts = true, framed = false, body = 'table',
  renderMore, layoutMenuItems, actionMenuItems,
}: Props) {
  // Who the rows' user-key columns point at, named by the engine — so a column
  // headed "Asked by" reads as a person rather than `443a07c7-526c-…`.
  const people = usePeople()
  // Show every visible column from the metadata, ordered by the designer's
  // left-to-right grid position (x), with any live width/order override applied
  // on top (drag to resize / reorder — see useResolvedGridLayout).
  const baseCols = useMemo(() => listOrderedColumns(model), [model])
  // Customize mode: ⚙ on every header, the freeze saved for the organization,
  // and the columns not in the list in a tray under the grid.
  const cz = useTxnCustomize()
  const czForm = cz ? (formNo || model.form_no || '1') : null
  const rl = useResolvedGridLayout(gridLayout, formNo)
  const cols = useMemo(() => orderedDisplayCols(baseCols, rl.layout), [baseCols, rl.layout])
  const wOf = useCallback((c: ColumnDef) => widthOf(c, rl.layout), [rl.layout])

  // System add / edit overrides: relabel the built-in Add button / retitle
  // the row pencil and route the click through the action runner so the
  // action's pre-action validations run first. Without a runner (host didn't
  // wire actions) the plain built-in behaviour is kept.
  const addOverride = onRunFormAction ? systemOverrides?.add : undefined
  const editOverride = onRunRowAction ? systemOverrides?.edit : undefined
  const viewOverride = onRunRowAction ? systemOverrides?.view : undefined
  const addLabel = addOverride?.name || 'Add'
  // Every way of opening a record (pencil/eye, row click, double-click, F4)
  // goes through here. An EDITABLE row routes through the edit system action
  // (its conditions gate opening for edit); a NON-editable row routes through
  // the VIEW system action (its conditions gate opening read-only) — the edit
  // action's conditions must NOT run when the record is only being viewed.
  const openEdit = useCallback((txnId: string) => {
    const row = rows.find(r => r._txn_id === txnId)
    openRecord({
      txnId,
      editable: !row || rowEditable(row),
      systemOverrides: { edit: editOverride, view: viewOverride },
      onRunRowAction,
      onOpen: (id) => onEdit(id),
    })
  }, [rows, editOverride, viewOverride, onRunRowAction, onEdit])
  // A row click: read-only when the host asked for it (onView), through the
  // view system action's gate like any other read-only open.
  const openRow = useCallback((txnId: string) => {
    if (!onView) { openEdit(txnId); return }
    openRecord({
      txnId,
      editable: false,
      systemOverrides: { view: viewOverride },
      onRunRowAction,
      onOpen: (id) => onView(id),
    })
  }, [onView, openEdit, viewOverride, onRunRowAction])
  // What a record's ⋮ offers — the same list on a table row and on a card.
  // Row-scoped custom actions run against THIS record, minus any the engine
  // hid for it (visible_when). A card has no pencil beside it, so it leads
  // with Edit / View (`withOpen`).
  const rowMenuActions = (row: RowShape, withOpen = false) => [
    ...(withOpen ? [{
      label: rowEditable(row) ? (editOverride?.name || 'Edit') : (rowLockReason(row) || 'View'),
      hook: rowEditable(row) ? 'edit' : 'view',
      icon: rowEditable(row) ? <Pencil size={14} /> : <Eye size={14} />,
      onClick: () => openEdit(row._txn_id),
    }] : []),
    ...visibleRowActions(rowActions, rowHiddenActions(row)).map((a) => ({
      label: a.name,
      hook: a.name,
      icon: <Play size={14} />,
      shortcut: shortcutHint(actionShortcutCombo(a)) || undefined,
      onClick: () => onRunRowAction?.(a.name, row._txn_id),
    })),
    ...(onAssign ? [{ label: 'Assign to\u2026', hook: 'assign', icon: <UserPlus size={14} />, onClick: () => onAssign(row._txn_id) }] : []),
    ...(onAlias ? [{ label: 'Also known as', hook: 'alias', icon: <Tags size={14} />, onClick: () => onAlias(row._txn_id) }] : []),
    ...(onProcessTrail ? [{ label: 'Process trail', hook: 'trail', icon: <Waypoints size={14} />, onClick: () => onProcessTrail(row._txn_id) }] : []),
    ...(onHistory ? [{ label: 'Audit trail', hook: 'history', icon: <History size={14} />, onClick: () => onHistory(row._txn_id) }] : []),
    ...(onMessages ? [{ label: 'Messages', hook: 'messages', icon: <Mail size={14} />, onClick: () => onMessages(row._txn_id) }] : []),
    ...(canDelete && (!systemOverrides?.delete || visibleRowActions([systemOverrides.delete], rowHiddenActions(row)).length) ? [{ label: systemOverrides?.delete?.name || 'Delete', hook: 'delete', icon: <Trash2 size={14} />, onClick: () => onDelete(row._txn_id), danger: true }] : []),
  ]
  const dnd = useColumnDnd({
    displayKeys: useMemo(() => cols.map(colKeyOf), [cols]),
    enabled: true,
    onReorder: rl.setOrder,
    onResize: rl.setWidth,
  })
  const ux: EmbedGridUx = gridUx || {}
  const grid = useGridState()
  // Inside the Run shell the expand button is the SHELL's full screen (all
  // chrome hidden, the screen fills the window); elsewhere the list overlays
  // itself as before.
  const chrome = useObjectChrome()
  const shellFs = fullScreen
    ?? (chrome?.fullScreen && objName && chrome.objName === objName ? chrome.fullScreen : null)
  const overlayFull = shellFs ? false : grid.isFullScreen
  const fullOn = shellFs ? shellFs.on : grid.isFullScreen
  const toggleFull = shellFs ? () => shellFs.set(!shellFs.on) : grid.toggleFullScreen
  // The Run shell draws its own full-screen button for the screen; a second
  // one here would do the same thing.
  const shellOwnsFullScreen = !fullScreen && !!shellFs

  // Apply search / filter / sort to the rows in-memory.
  const visibleRows = useMemo(() => {
    if (!grid.search && grid.filters.length === 0 && grid.sort.length === 0) {
      return rows
    }
    return applyGridState(rows, grid.search, grid.filters, grid.sort)
  }, [rows, grid.search, grid.filters, grid.sort])

  // ── Bulk multi-row selection ────────────────────────────────────────────
  // Available only when the host wired bulk actions. Selection is held by
  // _txn_id and pruned whenever the underlying rows change (e.g. after a bulk
  // run refreshes the list) so it never references gone records.
  const bulkEnabled = (bulkActions?.length || 0) > 0 && !!onRunBulkAction
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  // Anchor row for shift-click range selection (the last checkbox clicked
  // without Shift). Held by _txn_id so it survives reorders/filtering.
  const [anchorId, setAnchorId] = useState<string | null>(null)
  const [selectingAll, setSelectingAll] = useState(false)
  useEffect(() => {
    if (!bulkEnabled) return
    setSelectedIds(prev => {
      if (prev.size === 0) return prev
      const present = new Set(rows.map(r => r._txn_id))
      let changed = false
      const next = new Set<string>()
      prev.forEach(id => { if (present.has(id)) next.add(id); else changed = true })
      return changed ? next : prev
    })
  }, [rows, bulkEnabled])
  const toggleRowSelected = useCallback((txnId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(txnId)) next.delete(txnId)
      else next.add(txnId)
      return next
    })
  }, [])
  const clearSelection = useCallback(() => { setSelectedIds(new Set()); setAnchorId(null) }, [])

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  // F3 add · F4 edit · Ctrl+Delete delete (all on the keyboard-target row) ·
  // Ctrl+N / Ctrl+P page · Ctrl+F focus search. The target row is the one the
  // user last hovered/clicked, falling back to the host selection, then the
  // first visible row. Scoped to the list container so only the hovered/
  // focused list responds when several are on one page.
  const listRef = useRef<HTMLDivElement>(null)
  const [activeTxnId, setActiveTxnId] = useState<string | null>(null)
  const kbTargetId = activeTxnId ?? selectedTxnId ?? (visibleRows[0]?._txn_id ?? null)
  const doAdd = useCallback(() => {
    if (busy || !canAdd) return
    if (addOverride) onRunFormAction!(addOverride.name)
    else onAdd()
  }, [busy, canAdd, addOverride, onRunFormAction, onAdd])
  const focusSearch = useCallback(() => {
    listRef.current?.querySelector<HTMLInputElement>('input[data-grid-search]')?.focus()
  }, [])
  const pageStep = useCallback((dir: 1 | -1) => {
    const p = pagination
    if (!p) return
    if (p.mode === 'paged' && p.onGotoPage) {
      const page = p.page || 0
      const pageCount = Math.max(1, Math.ceil((p.total || 0) / Math.max(1, p.pageSize || 50)))
      const next = page + dir
      if (next >= 0 && next <= pageCount - 1) p.onGotoPage(next)
    } else if (dir === 1 && p.hasMore) {
      // Continuous mode has no "previous" — Ctrl+N appends the next (older) page.
      p.onLoadEarlier()
    }
  }, [pagination])
  // The designer's own keys on custom buttons (ActionDef.shortcut): a
  // form button runs as if clicked; a row button runs on the keyboard-target
  // row — unless the engine hid it for that row (visible_when). System keys
  // can never be taken (actionShortcutCombo ignores them), so these never
  // shadow the list's own. Form buttons first, then row buttons; a key two
  // buttons share goes to the first (the lint reports the clash).
  const customShortcuts: Shortcut[] = useMemo(() => {
    const out: Shortcut[] = []
    const taken = new Set<string>()
    if (onRunFormAction) {
      for (const b of actionShortcutBindings(formActions || [])) {
        taken.add(b.combo)
        out.push({ combo: b.combo, run: () => { if (!busy) onRunFormAction(b.name) } })
      }
    }
    if (onRunRowAction) {
      for (const b of actionShortcutBindings(rowActions || [])) {
        if (taken.has(b.combo)) continue
        taken.add(b.combo)
        out.push({
          combo: b.combo,
          run: () => {
            if (busy || !kbTargetId) return
            const row = rows.find(r => r._txn_id === kbTargetId)
            if (row && rowHiddenActions(row).includes(b.name)) return
            onRunRowAction(b.name, kbTargetId)
          },
        })
      }
    }
    return out
  }, [formActions, rowActions, onRunFormAction, onRunRowAction, busy, kbTargetId, rows])
  useKeyboardShortcuts(
    [
      { combo: systemCombo('add'), run: doAdd },
      { combo: systemCombo('edit'), run: () => { if (kbTargetId) openEdit(kbTargetId) } },
      { combo: systemCombo('delete'), run: () => { if (kbTargetId && canDelete) onDelete(kbTargetId) } },
      { combo: systemCombo('next_page'), run: () => pageStep(1) },
      { combo: systemCombo('previous_page'), run: () => pageStep(-1) },
      { combo: systemCombo('search'), run: focusSearch },
      ...customShortcuts,
    ],
    { enabled: shortcutsEnabled !== false, scopeRef: listRef },
  )

  const sortIndex = useMemo(() => {
    const m = new Map<string, { dir: 'asc' | 'desc'; idx: number }>()
    grid.sort.forEach((s, i) => m.set(s.column, { dir: s.dir, idx: i }))
    return m
  }, [grid.sort])

  // A total is authored against the column's db_name (the page designer's
  // key), while the footer now reads rows — and so looks its spec up — by
  // form key. Re-key once so a total configured on a column whose name and
  // db_name differ keeps its footer instead of quietly disappearing.
  const totalsByFormKey = useMemo(() => {
    if (!columnTotals) return undefined
    const out: Record<string, EmbedColumnTotal> = {}
    for (const c of cols) {
      const fk = columnFormKey(c)
      const spec = columnTotals[fk] ?? columnTotals[columnDbName(c)]
      if (spec) out[fk] = spec
    }
    return out
  }, [columnTotals, cols])

  const showTotals = ux.totals !== false && totalsByFormKey
    && Object.values(totalsByFormKey).some(Boolean)

  // Bulk selection derived from the currently visible rows.
  const selectedCount = selectedIds.size
  // The head folds away on a scroll down and returns on the first scroll up —
  // never while customizing (its controls are being arranged) or with rows
  // selected (the selection bar replaces the toolbar and must stay in view).
  const headRef = useRef<HTMLDivElement>(null)
  const recordsRef = useRef<HTMLDivElement>(null)
  const headHidden = useScrollAwayHeader(recordsRef, headRef, !cz && selectedCount === 0 && visibleRows.length > 0)
  // Clipped while folded and while it animates; visible once open, so the
  // toolbar's own drop-downs are never cut off. Settled by a timer rather
  // than transitionend, which never fires with reduced motion.
  const [headClip, setHeadClip] = useState(false)
  const headFirst = useRef(true)
  useEffect(() => {
    if (headFirst.current) { headFirst.current = false; return }
    setHeadClip(true)
    const t = setTimeout(() => setHeadClip(headHidden), prefersReducedMotion() ? 0 : HEAD_ANIMATION_MS + 40)
    return () => clearTimeout(t)
  }, [headHidden])
  const allVisibleSelected = bulkEnabled && visibleRows.length > 0
    && visibleRows.every(r => selectedIds.has(r._txn_id))
  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      const everySelected = visibleRows.length > 0
        && visibleRows.every(r => next.has(r._txn_id))
      if (everySelected) visibleRows.forEach(r => next.delete(r._txn_id))
      else visibleRows.forEach(r => next.add(r._txn_id))
      return next
    })
  }, [visibleRows])
  // A row checkbox click. Shift+click extends the selection from the anchor
  // (the last plain-clicked row) to here; a plain click toggles + re-anchors.
  const handleRowCheckboxClick = useCallback((e: React.MouseEvent, txnId: string) => {
    e.stopPropagation()
    if (e.shiftKey && anchorId) {
      const a = visibleRows.findIndex(r => r._txn_id === anchorId)
      const b = visibleRows.findIndex(r => r._txn_id === txnId)
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a <= b ? [a, b] : [b, a]
        setSelectedIds(prev => {
          const next = new Set(prev)
          for (let k = lo; k <= hi; k++) next.add(visibleRows[k]._txn_id)
          return next
        })
        setAnchorId(txnId)
        return
      }
    }
    toggleRowSelected(txnId)
    setAnchorId(txnId)
  }, [anchorId, visibleRows, toggleRowSelected])
  // The select-all-across-pages link is offered only when no client-side
  // search/column-filter is narrowing the loaded rows (the server can't honour
  // an in-memory filter), every loaded row is ticked, and more rows match on
  // the server than are loaded.
  const clientFilterActive = !!grid.search || grid.filters.length > 0
  const moreOnServer = bulkEnabled && !!onSelectAllMatching && !clientFilterActive
    && allVisibleSelected && total > visibleRows.length
  const doSelectAllMatching = useCallback(async () => {
    if (!onSelectAllMatching) return
    setSelectingAll(true)
    try {
      setSelectedIds(new Set(await onSelectAllMatching()))
    } finally {
      setSelectingAll(false)
    }
  }, [onSelectAllMatching])

  // Row density — this person's habit on this machine, like the editor size.
  const [dense, setDense] = useState<boolean>(() => {
    try { return window.localStorage.getItem(DENSE_KEY) === '1' } catch { return false }
  })
  const toggleDense = () => setDense((d) => {
    try { window.localStorage.setItem(DENSE_KEY, d ? '0' : '1') } catch { /* not remembered */ }
    return !d
  })
  const cellTd: React.CSSProperties = dense ? { ...td, padding: '7px 14px' } : td
  // The search scope's pieces, placed in this list's one toolbar row.
  const scopeParts = useEnforcedFilterParts({
    model, enforcedFilters, filterSets, activeFilterSetId, bypassed, busy,
    notices: filterNotices, onApplyFilters, onSelectFilterSet, project, objName,
  })
  const hasTabs = !!tabs && tabs.length > 0 && !!onSelectTab

  const narrowScreen = isMobileViewport()
  // The total is said ONCE — in the page bar under the records (and per tab).
  // Here only what the in-table search / column filters leave of the loaded
  // rows, while they narrow it.
  const narrowedCount = (!pagination || (clientFilterActive && visibleRows.length !== rows.length)) ? (
    <span style={{ color: 'var(--color-text-secondary, #465062)', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
      {clientFilterActive && visibleRows.length !== rows.length
        ? `${visibleRows.length} of ${rows.length} loaded match`
        : `${total.toLocaleString()} record${total === 1 ? '' : 's'}`}
    </span>
  ) : null
  // Row density lives in the ⋮ menu when the screen hands one over.
  const densityItem: MoreMenuItem = {
    label: dense ? 'Comfortable rows' : 'Compact rows',
    icon: dense ? <Rows3 size={14} /> : <Rows4 size={14} />,
    onClick: toggleDense,
    hook: 'density',
  }
  // The list's actions, at the end of the toolbar row inside the card:
  // layout save · Refresh · Approvals · form buttons · Add · full screen · ⋮.
  const listActions = (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, flex: 'none' }}>
      {!!rl.apply && rl.dirty && (
        <>
          <LayoutActions
            show
            applying={rl.applying}
            onReset={rl.reset}
            onApply={rl.apply ?? (() => {})}
            applyLabel={rl.applyLabel}
            applyTitle={rl.applyTitle}
          />
          <span style={toolbarDivider} aria-hidden />
        </>
      )}
      {!showTitle && narrowedCount}
      <button type="button" onClick={onRefresh} disabled={busy} title="Refresh" aria-label="Refresh"
              {...tw('action', 'refresh')} style={headerIconBtn}>
        <RefreshCcw size={17} />
      </button>
      {/* Secondary, list-wide affordances (e.g. the Approvals inbox) sit
          between Refresh and the primary Add button. */}
      {beforeAddActions}
      {/* Form-scoped custom actions (generic — not tied to a row) render
          just before Add, per the action's resolved scope. */}
      {(formActions || []).map((a) => (
        <button
          key={`fa-${a.name}`}
          type="button"
          {...tw('action', a.name, { scope: 'form' })}
          onClick={() => onRunFormAction?.(a.name)}
          disabled={busy}
          title={withShortcutHint(a.name, actionShortcutCombo(a))}
          style={headerBtn}
        >
          <Play size={15} /> {a.name}
        </button>
      ))}
      {canAdd && (
        <button
          type="button"
          /* `doAdd` (never `onAdd` directly): `onAdd` is TxnScreen's `startAdd`,
             whose parameter is an OPTIONAL seed object. TypeScript accepts that
             for an `onAdd: () => void` prop, so binding it straight to onClick
             type-checks while React hands it the MouseEvent as the seed — the
             event's own keys then land in the form values (mapSeedToFormKeys
             deliberately keeps unmapped keys) and the save body fails to
             serialize on `target`'s React fiber cycle. doAdd takes no
             parameters and already carries this same addOverride branch. */
          onClick={doAdd}
          disabled={busy}
          title={withShortcutHint(addOverride?.name || 'Add', systemCombo('add'))}
          {...tw('action', 'add')}
          style={headerPrimaryBtn}
        >
          <Plus size={16} /> {addLabel}
        </button>
      )}
      {/* Full screen — the Run shell's own button does it there. */}
      {!shellOwnsFullScreen && (
        <button
          type="button"
          {...tw('action', 'fullscreen')}
          onClick={toggleFull}
          aria-pressed={fullOn}
          aria-label={fullOn ? 'Exit full screen' : 'Full screen'}
          title={fullOn ? 'Exit full screen' : 'Full screen'}
          style={headerIconBtn}
        >
          {fullOn ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
        </button>
      )}
      {!renderMore && actionMenuItems && actionMenuItems.length > 0 && (
        <span title="More actions" {...tw('action', 'screen-actions-menu')}>
          <RowActionsMenu circled actions={actionMenuItems.map(i => ({
            label: i.label, icon: i.icon, onClick: i.onClick, hook: i.hook, shortcut: i.shortcut,
          }))} />
        </span>
      )}
      {renderMore
        ? renderMore(withActionsFirst(actionMenuItems, [...(layoutMenuItems || []), densityItem]))
        : headerActions}
    </span>
  )
  const containerStyle: React.CSSProperties = overlayFull ? {
    // Above the Run view's sticky top bar (zIndex 9000) so the fullscreen
    // grid isn't truncated behind it; preserves order under the editor family.
    position: 'fixed', inset: 0, zIndex: 9410,
    background: 'var(--color-bg)',
    display: 'flex', flexDirection: 'column',
    ...(framed ? { padding: screenMargin({ overlay: true }), gap: 16 } : {}),
  } : {
    // Fill the parent as a flex child rather than `height: 100%`. `height:100%`
    // needs a parent with a DEFINITE height; an embed box that only sets
    // `maxHeight` (to grow with data up to a cap) is indefinite, so `height:100%`
    // would collapse to content and the inner grid wouldn't scroll. `flex:1 +
    // minHeight:0` respects a flex parent's max-height clamp, so the grid scrolls
    // inside while the toolbar/header stay put. Both consumers (LivePreviewTab,
    // EmbedLivePreview) wrap ListView in a flex column.
    display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
    ...(framed ? {
      background: 'var(--color-bg, #eef1f5)',
      // Inside a host that names the screen (the Run shell) the host already
      // leaves a margin round its screens — adding ours doubled it. Elsewhere
      // a small one; a phone keeps its width for the records.
      padding: screenMargin({ hosted: !showTitle, narrow: narrowScreen }), gap: narrowScreen ? 6 : 10,
    } : {}),
  }

  return (
    <div ref={listRef} style={containerStyle}>
      {/* The title (and what a search leaves of the loaded rows) — only where
          the screen names itself; inside the Run shell its own bar does. */}
      {showTitle && (
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 12, minWidth: 0, flex: 'none',
          ...(framed ? {} : { padding: '10px 16px', borderBottom: '1px solid var(--color-border, #dde3ea)' }),
        }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, fontWeight: 600, color: 'var(--color-text, #1c2430)' }}>
            {model.form_title || model.main_table}
          </h1>
          {narrowedCount}
        </div>
      )}

      {/* The card: tabs · toolbar · records · page bar. */}
      <div style={framed ? cardStyle : flatCardStyle}>
      {/* The head — tabs row + toolbar. It folds away while the records are
          scrolled down and returns on the first scroll back up
          (useScrollAwayHeader); it sits outside the scrolling area, so it
          comes back where it always is. A selection keeps it in view. */}
      <div style={headFoldStyle(headHidden, headClip)} aria-hidden={headHidden || undefined}>
      <div ref={headRef} style={headInnerStyle(headHidden, headClip)} inert={headHidden || undefined}>
      {/* Row 1 — the TABS (list_view.tabs) with the list layout switch at
          the end of the row. Not drawn when it would hold nothing (no tabs,
          and the switch kept in the ⋮ menu). */}
      {(hasTabs || (framed && viewSwitcher)) && (
        <ListTabs tabs={hasTabs ? tabs! : []} active={activeTab ?? null} onSelect={onSelectTab ?? (() => {})}
                  showCounts={showTabCounts} busy={busy} trailing={viewSwitcher} />
      )}

      {/* Row 2 — ONE toolbar (shared with Spread sheet and Bulk entry). A
          selection replaces it. */}
      {!(bulkEnabled && selectedCount > 0) && (
        <ListToolbar
          scope={scopeParts.scope}
          chips={scopeParts.chips}
          columns={cols}
          columnKeyOf={columnFormKey}
          {...(ux.filter !== false ? {
            filters: grid.filters, onUpsertFilter: grid.upsertFilter,
            onRemoveFilter: grid.removeFilter, onClearFilters: grid.clearFilters,
          } : {})}
          {...(ux.sort !== false ? { sort: grid.sort, onClearSort: grid.clearSort } : {})}
          {...(ux.search !== false ? { search: grid.search, onSearch: grid.setSearch } : {})}
          dense={dense}
          {...(renderMore ? {} : { onToggleDense: toggleDense })}
          actions={listActions}
          trailing={!hasTabs && !framed ? viewSwitcher : undefined}
        />
      )}
      {scopeParts.below}
      </div>
      </div>

      {bulkEnabled && selectedCount > 0 && (
        <div style={selectionBarStyle}>
          <span style={{ fontSize: '0.9375rem', whiteSpace: 'nowrap' }}>
            <strong>{selectedCount}</strong> selected
          </span>
          {moreOnServer && (
            <button
              className="btn btn-link btn-sm"
              onClick={doSelectAllMatching}
              disabled={selectingAll || bulkBusy}
              title={`Select all ${total} records matching the current filters`}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: 'var(--color-accent)', cursor: 'pointer',
                fontSize: '0.9125rem', whiteSpace: 'nowrap', textDecoration: 'underline',
              }}
            >
              {selectingAll ? 'Selecting…' : `Select all ${total}`}
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
            {(bulkActions || []).map(a => (
              <button
                key={`bulk-${a.name}`}
                {...tw('action', a.name, { scope: 'bulk' })}
                className="btn btn-primary btn-sm"
                disabled={bulkBusy}
                onClick={() => onRunBulkAction!(a.name, Array.from(selectedIds))}
                title={`Run "${a.name}" on the ${selectedCount} selected record${selectedCount === 1 ? '' : 's'}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: bulkBusy ? 'wait' : 'pointer' }}
              >
                <Play size={13} /> {a.name}
              </button>
            ))}
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={clearSelection}
            disabled={bulkBusy}
            {...tw('action', 'clear_selection')}
            title="Clear selection"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <X size={13} /> Clear
          </button>
        </div>
      )}

      <div ref={recordsRef} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {visibleRows.length === 0 ? (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <div style={{
              padding: 40, textAlign: 'center',
              color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
            }}>
              {rows.length > 0 ? (
                <>
                  <div style={{ fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>
                    No records match the current filters / search
                  </div>
                  <button type="button" className="btn btn-link btn-sm" {...tw('action', 'filter-clear')}
                          onClick={() => { grid.setSearch(''); grid.clearFilters() }}
                          style={{ background: 'none', border: 0, color: 'var(--color-accent)', cursor: 'pointer' }}>
                    Clear search and filters
                  </button>
                </>
              ) : total === 0 && !enforcedFilters?.length && !(activeTab && activeTab !== ALL_TAB_ID) ? (
                // First run: nothing saved yet on this screen.
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: 440, margin: '0 auto' }}>
                  <span aria-hidden style={{
                    display: 'inline-flex', width: 64, height: 64, borderRadius: 16,
                    background: 'var(--color-accent-weak)', color: 'var(--color-accent)',
                    alignItems: 'center', justifyContent: 'center',
                  }}><FileText size={30} /></span>
                  <div style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-text)' }}>
                    No {(model.form_title || model.main_table || 'records')} yet
                  </div>
                  {canAdd && (
                    <button className="btn btn-primary" onClick={doAdd} {...tw('action', 'add_first')}>
                      <Plus size={16} /> {addLabel}
                    </button>
                  )}
                </div>
              ) : 'No records match this tab or search scope.'}
            </div>
          </div>
        ) : body === 'cards' ? (
          <div style={{
            flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 20px',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 12, alignContent: 'start',
          }}>
            {visibleRows.map((row, i) => (
              <div key={row._txn_id} {...tw('row', i, { key: row._txn_id })}>
                <CompactSummaryCard sm={model} row={row} selected={selectedTxnId === row._txn_id}
                                    onClick={() => openRow(row._txn_id)}
                                    trailing={<RowActionsMenu circled actions={rowMenuActions(row, true)} />} />
              </div>
            ))}
          </div>
        ) : (
          <SplitScrollTable
            ref={splitHandle}
            // While customizing, show (and save) the organization's freeze,
            // not this person's own.
            storageKey={czForm ? undefined : `twasta:split:list:${cols[0]?.table_name || model.form_no || 'main'}`}
            dataCount={cols.length}
            defaultFrozen={Number(model.frozen_columns) || 0}
            onSplitChange={cz && czForm ? (n) => {
              if (n === (Number(model.frozen_columns) || 0)) return
              void cz.session.apply(czSetLeaf(czFormPath(czForm, 'frozen_columns'), n),
                n ? `Freeze ${n} column${n === 1 ? '' : 's'}` : 'Unfreeze')
            } : undefined}
          >
            {(range) => {
              const paneCols = cols.slice(range.colStart, range.colEnd)
              const colsWidth = paneCols.reduce((sum, c) => sum + wOf(c),
                (range.isTrailing ? ACTIONS_COL_WIDTH : 0)
                  + (range.isTrailing && rowAdornment ? ADORNMENT_COL_WIDTH : 0)
                  + (range.isLeading && bulkEnabled ? SELECT_COL_WIDTH : 0))
              return (
              <table className={LIST_GRID_CLASS} style={{
                borderCollapse: 'collapse', fontSize: '0.9375rem',
                // The card's own surface: the cells paint it (LIST_GRID_CSS),
                // so the rows read as one piece with the card around them.
                background: 'var(--color-bg-secondary, #ffffff)',
                // ``fixed`` layout makes the browser honour the explicit
                // <colgroup> widths instead of distributing based on cell
                // content — pixel-accurate match with the designer.
                tableLayout: 'fixed',
                // The last pane runs to the card's edge: the filler column
                // takes what the columns leave, so the heading band and the
                // row lines are not cut short and the actions sit at the edge.
                width: range.isTrailing ? `max(${colsWidth}px, 100%)` : colsWidth,
              }}>
                <colgroup>
                  {range.isLeading && bulkEnabled && <col style={{ width: SELECT_COL_WIDTH }} />}
                  {paneCols.map(c => (
                    <col
                      key={c.col_id || c.name}
                      data-col-key={colKeyOf(c)}
                      style={{ width: wOf(c) }}
                    />
                  ))}
                  {range.isTrailing && rowAdornment && <col style={{ width: ADORNMENT_COL_WIDTH }} />}
                  {range.isTrailing && <col />}
                  {range.isTrailing && <col style={{ width: ACTIONS_COL_WIDTH }} />}
                </colgroup>
                <thead style={{ position: 'sticky', top: 0, background: GRID_BAND, zIndex: 1 }}>
              <tr>
                {range.isLeading && bulkEnabled && (
                  <th style={{ ...th, textAlign: 'center', cursor: 'default' }}>
                    <input
                      type="checkbox"
                      aria-label="Select all rows"
                      checked={allVisibleSelected}
                      ref={el => { if (el) el.indeterminate = !allVisibleSelected && selectedCount > 0 }}
                      onChange={toggleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                {paneCols.map((c, i) => {
                  const key = columnFormKey(c)
                  const ck = colKeyOf(c)
                  const ss = sortIndex.get(key)
                  const sortable = ux.sort !== false
                  const ind = dnd.dropIndicator?.key === ck ? dnd.dropIndicator.side : null
                  return (
                    <th
                      key={c.col_id || c.name}
                      data-split-col={range.colStart + i}
                      {...dnd.getHeaderProps(ck)}
                      style={{
                        ...th,
                        position: 'relative',
                        cursor: 'grab',
                        userSelect: 'none',
                        boxShadow: ind === 'left'
                          ? 'inset 3px 0 0 var(--color-accent)'
                          : ind === 'right'
                            ? 'inset -3px 0 0 var(--color-accent)'
                            : undefined,
                      }}
                      onClick={sortable ? (e) => grid.toggleSort(key, e.shiftKey) : undefined}
                      title={sortable
                        ? 'Drag to reorder · drag the right edge to resize · click to sort (shift-click adds a column)'
                        : 'Drag to reorder · drag the right edge to resize'}
                    >
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        {listHeadingOf(c)}
                        {cz && czForm && cz.session.countCustomized(czColPath(czForm, ck, 'x').slice(0, -2)) > 0 && <CustomizedDot />}
                        {ss && (
                          <span style={{
                            color: 'var(--color-accent, #6366f1)',
                            fontWeight: 700,
                            display: 'inline-flex', alignItems: 'center',
                          }}>
                            {ss.dir === 'asc' ? <ArrowUpAZ size={11} /> : <ArrowDownAZ size={11} />}
                            {grid.sort.length > 1 && (
                              <sup style={{ marginLeft: 2 }}>{ss.idx + 1}</sup>
                            )}
                          </span>
                        )}
                      </span>
                      {cz && czForm && (
                        <GearButton label={`Change ${listHeadingOf(c)}`}
                          active={cz.openFieldKey === `${czForm}|${ck}`}
                          onClick={(el) => cz.openField(czForm, ck, el)}
                          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }} />
                      )}
                      <div {...dnd.getResizeHandleProps(ck)} style={RESIZE_HANDLE_STYLE} />
                    </th>
                  )
                })}
                {range.isTrailing && rowAdornment && (
                  <th style={{ ...th, cursor: 'default' }}>{rowAdornmentHeading || ''}</th>
                )}
                {range.isTrailing && <th aria-hidden="true" style={{ ...th, padding: 0 }} />}
                {range.isTrailing && (
                  <th style={{
                    ...th, textAlign: 'right',
                    position: 'sticky', right: 0,
                  }}></th>
                )}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, rowIndex) => {
                const isSelected = selectedTxnId === row._txn_id
                const isBulkSelected = bulkEnabled && selectedIds.has(row._txn_id)
                const isKbTarget = !isSelected && activeTxnId != null && kbTargetId === row._txn_id
                // Hover / selection / keyboard target are painted on the CELLS
                // by LIST_GRID_CSS — every cell alike, the sticky actions cell
                // included — never on the row behind them.
                const rowCls = [
                  (isSelected || isBulkSelected) && 'tw-sel',
                  isKbTarget && range.isLeading && 'tw-kb',
                ].filter(Boolean).join(' ') || undefined
                return (
                  <tr
                    key={row._txn_id}
                    className={rowCls}
                    data-split-row={row._txn_id}
                    {...tw('row', rowIndex, { key: row._txn_id })}
                    onClick={() => {
                      // Single click: select-only when the embed page
                      // designer wires it up; otherwise legacy edit-on-click
                      // (transaction designer's standalone Live Preview tab).
                      if (onSelect) onSelect(row)
                      else openRow(row._txn_id)
                    }}
                    onDoubleClick={() => {
                      // Double click always opens the editor — both for
                      // select-aware callers (so users can still drill in)
                      // and for legacy edit-on-click callers (where it's a
                      // harmless no-op duplicate of the single click).
                      openRow(row._txn_id)
                    }}
                    style={{
                      cursor: 'pointer',
                      userSelect: onSelect ? 'none' : undefined,
                    }}
                    onMouseEnter={() => setActiveTxnId(row._txn_id)}
                  >
                    {range.isLeading && bulkEnabled && (
                      <td
                        style={{ ...cellTd, textAlign: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          aria-label="Select row"
                          checked={isBulkSelected}
                          onChange={() => {}}
                          onClick={(e) => handleRowCheckboxClick(e, row._txn_id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                    )}
                    {paneCols.map(c => (
                      <td key={c.col_id || c.name}
                          data-tw-cell={columnFormKey(c)}
                          style={{
                            ...cellTd,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                          }}
                          title={renderTitle(row[columnFormKey(c)], c, row, cols, people)}
                      >
                        {renderCell(row[columnFormKey(c)], c, row, cols, people)}
                      </td>
                    ))}
                    {range.isTrailing && rowAdornment && (
                      <td style={{ ...cellTd, whiteSpace: 'nowrap' }} data-tw-cell="_flags">
                        {rowAdornment(row)}
                      </td>
                    )}
                    {range.isTrailing && <td aria-hidden="true" style={{ ...cellTd, padding: 0 }} />}
                    {range.isTrailing && (
                    <td style={{
                      ...cellTd, padding: dense ? '2px 8px' : '4px 8px', textAlign: 'right',
                      position: 'sticky', right: 0,
                    }} onClick={(e) => e.stopPropagation()}>
                      {/* Edit shows on the row being pointed at (a click on
                          the row opens it too); a view-only row always shows
                          its eye, with the reason. */}
                      <button className="btn btn-ghost btn-sm"
                              {...tw('action', rowEditable(row) ? 'edit' : 'view')}
                              onClick={() => openEdit(row._txn_id)}
                              title={rowEditable(row)
                                ? (editOverride?.name || 'Edit')
                                : (rowLockReason(row) || 'View only')}
                              aria-label={rowEditable(row) ? (editOverride?.name || 'Edit') : 'View only'}
                              style={{ padding: 4, marginRight: 2,
                                opacity: !rowEditable(row) || activeTxnId === row._txn_id || isSelected ? 1 : 0 }}>
                        {rowEditable(row) ? <Pencil size={15} /> : <Eye size={15} />}
                      </button>
                      <RowActionsMenu circled actions={rowMenuActions(row)} />
                    </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
            {showTotals && (
              <tfoot>
                <ColumnTotalsRow
                  columns={paneCols}
                  columnKeyOf={columnFormKey}
                  columnWidthOf={wOf}
                  rows={visibleRows}
                  totals={totalsByFormKey!}
                  leadingCells={range.isLeading && bulkEnabled
                    ? <td style={{ background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
                    : undefined}
                  trailingCell={range.isTrailing
                    ? <>
                      {rowAdornment && <td style={{ background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />}
                      <td style={{ background: 'var(--color-surface-2, var(--color-bg-secondary))', padding: 0 }} />
                      <td style={{ background: 'var(--color-surface-2, var(--color-bg-secondary))' }} />
                    </>
                    : undefined}
                />
              </tfoot>
            )}
              </table>
              )
            }}
          </SplitScrollTable>
        )}
        <style>{LIST_GRID_CSS}</style>
        {cz && czForm && (
          <HiddenTray
            title="Not in the list"
            action="Add"
            hint="Use ⚙ → Show in list to take a column out. The field stays on the form."
            items={(model.columns || []).map(w => w.column).filter(c => c && isListHidden(c) && !c.uda).map(c => ({
              key: colKeyOf(c),
              label: String(c.heading || c.name),
              action: 'Add',
              onShow: () => void cz.session.apply(
                czSetLeaf(czColPath(czForm, colKeyOf(c), 'list_hidden'), false), `Add column ${c.heading || c.name}`),
            }))}
          />
        )}
        {pagination && (
          <PaginationBar
            pagination={pagination}
            busy={busy}
            hasRows={rows.length > 0}
          />
        )}
      </div>
      </div>
    </div>
  )
}


// Vertical separator used in the list header to set the primary action apart
// from the secondary button group.
const toolbarDivider: React.CSSProperties = {
  width: 1, height: 20, margin: '0 2px',
  background: 'var(--color-border)', flexShrink: 0,
}

// Width of the trailing actions column — just enough for the Edit pencil and
// the kebab menu. Kept in one place since it's referenced by both the
// <colgroup> entry and the table's total-width sum, which must stay in sync.
const ACTIONS_COL_WIDTH = 72

// Row density memory (per browser).
const DENSE_KEY = 'twasta.txn.list.dense'

// The card the list lives in, on the page background (light: white on grey).
// A firm edge and a soft shadow: the heading band and the rows run to the
// card's sides, so without them the table melts into the page.
export const screenCardStyle: React.CSSProperties = {
  flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
  background: 'var(--color-bg-secondary, #ffffff)',
  border: '1px solid var(--color-border-strong, #c7cfd9)', borderRadius: 12, overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.08)',
}
const cardStyle = screenCardStyle
const flatCardStyle: React.CSSProperties = {
  ...cardStyle, border: 0, borderRadius: 0, boxShadow: 'none',
}
// Header buttons: a bordered secondary, the solid primary, a quiet icon.
/** The screen's ⋮-placed actions (under their "Actions" heading) ahead of the
 *  list's own menu items, which start a new group so they do not read as
 *  actions. */
function withActionsFirst(actions: MoreMenuItem[] | undefined, rest: MoreMenuItem[]): MoreMenuItem[] {
  if (!actions || !actions.length) return rest
  const [first, ...more] = rest
  return first ? [...actions, { ...first, divider: !first.heading }, ...more] : [...actions]
}

const headerBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px',
  borderRadius: 8, border: '1px solid var(--color-border-strong, #c7cfd9)',
  background: 'var(--color-bg-secondary, #ffffff)', color: 'var(--color-text, #1c2430)',
  fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
}
const headerPrimaryBtn: React.CSSProperties = {
  ...headerBtn, border: '1px solid transparent',
  background: 'var(--color-accent, #0061D0)', color: '#ffffff',
}
const headerIconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38,
  borderRadius: 8, border: '1px solid transparent', background: 'transparent',
  color: 'var(--color-text-secondary, #465062)', cursor: 'pointer', padding: 0,
}

// Width of the leading checkbox column shown when bulk actions are wired.
const SELECT_COL_WIDTH = 40

// The bulk-action band shown above the grid once one or more rows are ticked.
const selectionBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '8px 16px',
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2, var(--color-bg-secondary))',
}

// The records grid's shades, mixed from the theme tokens toward the card
// surface so they follow light and dark alike. In light they come out as the
// prototype's: heading band #f6f8fa, row lines #e7ebf0, hover #f6f8fb,
// selection #eef4fd — the full-strength band and border made the rows look
// cut into pieces against the white card.
const GRID_SURFACE = 'var(--color-bg-secondary, #ffffff)'
const GRID_BAND = `color-mix(in srgb, var(--color-surface-2, #f0f3f7) 60%, ${GRID_SURFACE})`
const GRID_LINE = `color-mix(in srgb, var(--color-border, #dde3ea) 70%, ${GRID_SURFACE})`
const GRID_SELECTED = `color-mix(in srgb, var(--color-accent, #0061D0) 7%, ${GRID_SURFACE})`

// Body cells are painted here rather than inline so a row's hover and
// selection reach EVERY cell — the sticky actions cell too, which must stay
// opaque to cover what scrolls under it. Beats the global
// `table tr:hover td` rule on specificity; selection is listed last so a
// hovered selected row stays selected.
const LIST_GRID_CLASS = 'tw-list-grid'
const LIST_GRID_CSS = [
  `.${LIST_GRID_CLASS} > tbody > tr > td{background:${GRID_SURFACE}}`,
  `.${LIST_GRID_CLASS} > tbody > tr:hover > td{background:${GRID_BAND}}`,
  `.${LIST_GRID_CLASS} > tbody > tr.tw-sel > td{background:${GRID_SELECTED}}`,
  // Keyboard target (F4 edit / Ctrl+Delete): a left accent bar on the row.
  `.${LIST_GRID_CLASS} > tbody > tr.tw-kb > td:first-child{box-shadow:inset 3px 0 0 var(--color-accent, #0061D0)}`,
].join('\n')

const th: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  // Row lines only — a list reads across, it is not a spreadsheet.
  borderBottom: '1px solid var(--color-border, #dde3ea)',
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary, #465062)',
  background: GRID_BAND,
  // One line, like the cells under it — a long heading is cut, not wrapped.
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  // Render author-supplied headings in their authored case
  // (overrides global `table th { text-transform: uppercase }`).
  textTransform: 'none',
  letterSpacing: 'normal',
}

const td: React.CSSProperties = {
  padding: '12px 14px',
  color: 'var(--color-text, #1c2430)',
  borderBottom: `1px solid ${GRID_LINE}`,
}

// Plain-text tooltip for cells whose value is truncated by ellipsis.
function renderTitle(value: unknown, col: ColumnDef, row?: Record<string, unknown>, cols?: ColumnDef[],
                     people?: PeopleDirectory | null): string {
  const person = personLabel(people, columnFormKey(col), value)
  if (person) return person
  if (hasDisplayFormat(col)) return displayPlainText(value, col, row, cols)
  const s = formatCell(value, col)
  return s === '—' ? '' : s
}

// Rich cell rendering for presentation widgets: image columns show a
// thumbnail, document/url columns show a clickable link. A configured
// per-column display format (the designer "Display as" panel) takes over via
// the shared <DisplayCell>. Everything else falls back to plain-text formatCell.
function renderCell(value: unknown, col: ColumnDef, row?: Record<string, unknown>, cols?: ColumnDef[],
                    people?: PeopleDirectory | null): React.ReactNode {
  // A user-key column reads as the person, never as the key. Ahead of every
  // other rule: the stored value is an identifier, so no format applies to it.
  const person = personLabel(people, columnFormKey(col), value)
  if (person) return person
  if (hasDisplayFormat(col)) {
    return <DisplayCell value={value} col={col} row={row} columns={cols} />
  }
  const s = value == null ? '' : String(value).trim()
  if (s) {
    if (hasImageUpload(col)) {
      return (
        <img
          src={s}
          alt={displayNameForUrl(s)}
          style={{ height: 28, width: 28, objectFit: 'cover', borderRadius: 4, verticalAlign: 'middle' }}
        />
      )
    }
    if (hasDocumentUpload(col) || hasUrlWidget(col)) {
      const label = hasDocumentUpload(col) ? displayNameForUrl(s) : s
      return (
        <a
          href={s}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          style={{ color: 'var(--color-accent)', textDecoration: 'underline' }}
        >
          {label}
        </a>
      )
    }
  }
  return formatCell(value, col)
}

function formatCell(value: unknown, col: ColumnDef): string {
  if (value === null || value === undefined) return '—'
  const rich = richCellText(value, col)
  if (rich != null) return rich || '—'
  const when = temporalCellText(value, col)
  if (when != null) return when

  // For ddlb / radio / checkbox edit-masks the stored value is the "data"
  // code; show the user-facing "display" label instead.
  const label = ddlbDisplayLabel(value, col)
  if (label != null) return label

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (value instanceof Date) return value.toLocaleString()
  return String(value)
}
